import * as React from "react";
import ReactSelect, {
  ActionMeta,
  ClearIndicatorProps,
  components,
  DropdownIndicatorProps,
  GroupBase,
  MultiValue,
  MultiValueProps,
  PlaceholderProps,
  Props as ReactSelectProps,
} from "react-select";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { FormMessage } from "./form";
import { Label } from "./label";

export type OptionType = {
  label: string;
  value: string;
};

export interface MultiSelectProps
  extends Omit<ReactSelectProps<OptionType, true, GroupBase<OptionType>>, "classNames" | "components"> {
  options: OptionType[];
  value?: MultiValue<OptionType>;
  onChange?: (value: MultiValue<OptionType>, actionMeta: ActionMeta<OptionType>) => void;
  placeholder?: string;
  className?: string;
  containerClassName?: string;
  label?: string;
  description?: string;
  error?: string;
  disabled?: boolean;
}

const MultiValueComponent = ({
  children: _,
  data,
  removeProps,
}: MultiValueProps<OptionType, true, GroupBase<OptionType>>) => {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (removeProps.onClick) {
      removeProps.onClick(e as unknown as React.MouseEvent<HTMLDivElement>);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLButtonElement>) => {
    if (removeProps.onTouchEnd) {
      removeProps.onTouchEnd(e as unknown as React.TouchEvent<HTMLDivElement>);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (removeProps.onMouseDown) {
      removeProps.onMouseDown(e as unknown as React.MouseEvent<HTMLDivElement>);
    }
  };

  return (
    <div className="mr-1 flex items-center gap-1 rounded-sm px-1 py-0.5 text-sm">
      <span>{data.label}</span>
      <button
        type="button"
        aria-label={`Remove ${data.label}`}
        className="ml-1 flex items-center justify-center rounded-sm hover:bg-accent hover:text-accent-foreground"
        onClick={handleClick}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
};

const Placeholder = (props: PlaceholderProps<OptionType, true, GroupBase<OptionType>>) => {
  return (
    <components.Placeholder {...props}>
      <span className="text-muted-foreground">{props.children}</span>
    </components.Placeholder>
  );
};

const DropdownIndicator = (props: DropdownIndicatorProps<OptionType, true, GroupBase<OptionType>>) => (
  <components.DropdownIndicator {...props}>
    <ChevronDown className="h-4 w-4 opacity-50" />
  </components.DropdownIndicator>
);

const ClearIndicator = (props: ClearIndicatorProps<OptionType, true, GroupBase<OptionType>>) => (
  <components.ClearIndicator {...props}>
    <X className="h-4 w-4 opacity-50" />
  </components.ClearIndicator>
);

const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      className,
      containerClassName,
      options,
      value,
      onChange,
      placeholder,
      label,
      description,
      error,
      disabled,
      ...props
    },
    ref,
  ) => {
    const id = React.useId();
    const errorId = `${id}-error`;
    const descriptionId = `${id}-description`;

    return (
      <div ref={ref} className={cn("space-y-2", containerClassName)}>
        {label && <Label htmlFor={id}>{label}</Label>}
        {description && (
          <p id={descriptionId} className="text-sm text-muted-foreground">
            {description}
          </p>
        )}

        <ReactSelect<OptionType, true, GroupBase<OptionType>>
          inputId={id}
          aria-describedby={description ? descriptionId : undefined}
          aria-invalid={!!error}
          aria-errormessage={error ? errorId : undefined}
          isMulti
          options={options}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          isDisabled={disabled}
          classNames={{
            control: ({ isFocused }) =>
              cn(
                "!flex !min-h-10 !w-full !items-center !justify-between !rounded-md !border !border-input !bg-background !px-3 !py-2 !text-sm !ring-offset-background !placeholder:text-muted-foreground !focus:outline-none !shadow-none hover:!border-input",
                isFocused ? "!focus:ring-2 !focus:ring-ring !focus:ring-offset-2" : "",
                error ? "!border-destructive !ring-destructive" : "",
                disabled ? "!cursor-not-allowed !opacity-50" : "",
              ),
            placeholder: () => "!text-muted-foreground",
            input: () => "!text-foreground",
            option: ({ isFocused, isSelected }) =>
              cn(
                "!relative !flex !w-full !cursor-default !select-none !items-center !rounded-sm !py-1.5 !pl-8 !pr-2 !text-sm !outline-none hover:!bg-accent hover:!text-accent-foreground",
                isFocused && !isSelected && "!focus:bg-accent !focus:text-accent-foreground",
                isSelected && "!bg-accent !text-accent-foreground",
                disabled && "!pointer-events-none !opacity-50",
              ),
            menu: () =>
              "!absolute !z-50 !max-h-96 !min-w-[8rem] !overflow-hidden !rounded-md !border !bg-popover !text-popover-foreground !shadow-md !data-[state=open]:animate-in !data-[state=closed]:animate-out !data-[state=closed]:fade-out-0 !data-[state=open]:fade-in-0 !data-[state=closed]:zoom-out-95 !data-[state=open]:zoom-in-95 !data-[side=bottom]:slide-in-from-top-2 !data-[side=left]:slide-in-from-right-2 !data-[side=right]:slide-in-from-left-2 !data-[side=top]:slide-in-from-bottom-2",
            valueContainer: () => "!flex !flex-wrap !items-center !gap-1",
            multiValue: () => "!bg-transparent",
            indicatorsContainer: () => "!flex !items-center",
            indicatorSeparator: () => "!hidden",
            menuList: () => "!p-1",
            noOptionsMessage: () => "!text-muted-foreground !py-1.5 !pl-2 !pr-2 !text-sm",
          }}
          components={{
            MultiValue: MultiValueComponent,
            DropdownIndicator,
            ClearIndicator,
            Placeholder,
            Option: ({ children, isSelected, ...props }) => (
              <components.Option {...props} isSelected={isSelected}>
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                  {isSelected && <Check className="h-4 w-4" />}
                </span>
                {children}
              </components.Option>
            ),
          }}
          className={className}
          unstyled
          {...props}
        />

        {error && <FormMessage id={errorId}>{error}</FormMessage>}
      </div>
    );
  },
);

MultiSelect.displayName = "MultiSelect";

export { MultiSelect };
